import React from 'react';

export const DEFAULT_PRODUCT_COLOR = '#2563eb';

export const FIELD_TYPE_OPTIONS = [
  { value: 'string', label: 'Kort tekst' },
  { value: 'text', label: 'Lang tekst' },
  { value: 'integer', label: 'Tall' },
  { value: 'boolean', label: 'Ja/nei' },
  { value: 'select', label: 'Valgliste' },
  { value: 'date', label: 'Dato' },
  { value: 'datetime', label: 'Dato og tid' },
  { value: 'location', label: 'Lokasjon' },
];

export const CUSTOMER_JOURNEY_STEP_TYPE_OPTIONS = [
  { value: 'checkbox', label: 'Oppgave' },
  { value: 'appointment', label: 'Avtale' },
  { value: 'log', label: 'Logg' },
];

export const CUSTOMER_JOURNEY_TASK_STATE_COLOR_OPTIONS = [
  { value: 'blue', label: 'Blå' },
  { value: 'orange', label: 'Oransje' },
  { value: 'green', label: 'Grønn' },
  { value: 'red', label: 'Rød' },
];

export const PRODUCT_SOURCE_LABELS = {
  manual: 'Manuell',
  website_scan: 'Webscan',
  shopify: 'Shopify',
  woocommerce: 'WooCommerce',
};

export const PRODUCT_STATUS_LABELS = {
  active: 'Aktiv',
  archived: 'Arkivert',
};

export const normalizeColor = (value) => {
  const raw = String(value || DEFAULT_PRODUCT_COLOR).trim();
  const normalized = raw.startsWith('#') ? raw : `#${raw}`;
  return /^#[0-9a-f]{6}$/i.test(normalized) ? normalized.toLowerCase() : DEFAULT_PRODUCT_COLOR;
};

const normalizeCustomerJourneyId = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9_]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .slice(0, 80);

export const createCustomerJourneyId = (prefix = 'step') => {
  const safePrefix = normalizeCustomerJourneyId(prefix) || 'step';
  const browserCrypto = typeof window !== 'undefined' ? window.crypto : null;
  const randomPart = typeof browserCrypto?.randomUUID === 'function'
    ? browserCrypto.randomUUID().replace(/-/g, '').slice(0, 24)
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 14)}`;
  return `${safePrefix}_${randomPart}`;
};

export const normalizeCustomerJourneyStepType = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'task') return 'appointment';
  return CUSTOMER_JOURNEY_STEP_TYPE_OPTIONS.some((option) => option.value === normalized)
    ? normalized
    : 'appointment';
};

const normalizeTaskStateColor = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (['red', 'orange', 'blue', 'green'].includes(normalized)) return normalized;
  if (['danger', 'error', 'not-approved', 'not_approved'].includes(normalized)) return 'red';
  if (['warning', 'planned', 'in-progress', 'in_progress'].includes(normalized)) return 'orange';
  if (['success', 'done', 'approved'].includes(normalized)) return 'green';
  return 'blue';
};

const normalizeTaskStateCompletion = (value) => {
  const raw = Number(value);
  if (!Number.isFinite(raw)) return 0;
  const percent = raw <= 1 && raw >= 0 ? raw * 100 : raw;
  return Math.max(0, Math.min(100, Math.round(percent)));
};

export const normalizeCustomerJourneyPriority = (value) => {
  const raw = Number(value);
  if (!Number.isFinite(raw)) return 0;
  return Math.max(0, Math.min(100, Math.round(raw)));
};

export const normalizeCustomerJourneyTaskInputType = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (['checkbox', 'check', 'boolean'].includes(normalized)) return 'checkmark';
  if (['text', 'textarea'].includes(normalized)) return 'comment';
  return ['toggle', 'checkmark', 'comment', 'date'].includes(normalized) ? normalized : 'toggle';
};

const normalizeBoolean = (value, fallback = false) => {
  if (typeof value === 'boolean') return value;
  if (value === 1 || value === '1') return true;
  if (value === 0 || value === '0') return false;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  return fallback;
};

export const createCustomerJourneyTaskState = (index = 0) => ({
  key: createCustomerJourneyId('status'),
  label: index === 0 ? 'Pågår' : '',
  completion: index === 0 ? 50 : 0,
  color: index === 0 ? 'orange' : 'blue',
});

export const createCustomerJourneyStatus = (index = 0) => ({
  key: createCustomerJourneyId('status'),
  label: index === 0 ? 'Godkjent' : '',
  color: index === 0 ? 'green' : 'blue',
  showProgressionStatus: true,
});

export const normalizeCustomerJourneyTaskStates = (value, options = {}) => {
  const preserveEmptyLabels = options?.preserveEmptyLabels === true;
  const sourceEntries = Array.isArray(value)
    ? value
    : value && typeof value === 'object'
      ? Object.entries(value).map(([key, config]) => ({ key, ...(config || {}) }))
      : [];
  const seen = new Set();
  return sourceEntries.map((entry, index) => {
    if (!entry || typeof entry !== 'object') return null;
    let key = normalizeCustomerJourneyId(entry.key || entry.id || entry.type || entry.value || entry.status || entry.label) || createCustomerJourneyId('status');
    while (seen.has(key)) {
      key = createCustomerJourneyId('status');
    }
    seen.add(key);
    const label = String(entry.label || entry.name || '');
    return {
      key,
      label: label.trim() ? label : preserveEmptyLabels ? '' : `Status ${index + 1}`,
      completion: normalizeTaskStateCompletion(entry.completion ?? entry.completionPercent ?? entry.completion_percent ?? entry.percent),
      color: normalizeTaskStateColor(entry.color || entry.tone),
    };
  }).filter(Boolean).slice(0, 20);
};

export const normalizeCustomerJourneyStatuses = (value) => {
  const sourceEntries = Array.isArray(value)
    ? value
    : value && typeof value === 'object'
      ? Object.entries(value).map(([key, config]) => ({ key, ...(config || {}) }))
      : [];
  const seen = new Set();
  return sourceEntries.map((entry, index) => {
    if (!entry || typeof entry !== 'object') return null;
    let key = normalizeCustomerJourneyId(entry.key || entry.id || entry.value || entry.status || entry.label) || createCustomerJourneyId('status');
    while (seen.has(key)) {
      key = createCustomerJourneyId('status');
    }
    seen.add(key);
    const label = String(entry.label || entry.name || '');
    return {
      key,
      label: label.trim() ? label : `Status ${index + 1}`,
      color: normalizeTaskStateColor(entry.color || entry.tone),
      showProgressionStatus: normalizeBoolean(entry.showProgressionStatus ?? entry.show_progression_status, true),
    };
  }).filter(Boolean).slice(0, 20);
};

export const createEmptyInboxField = () => ({
  key: '',
  label: '',
  type: 'string',
  required: false,
  collectInChat: false,
  showInInboxList: false,
  placeholder: '',
  helpText: '',
  defaultValue: '',
  options: [],
});

export const createEmptyInboxConfig = () => ({
  fields: [],
  summaryFieldKeys: [],
});

export const createEmptyCustomerJourneyField = () => ({
  id: createCustomerJourneyId('field'),
  label: '',
  type: 'string',
  required: true,
  placeholder: '',
  helpText: '',
  options: [],
});

export const createEmptyCustomerJourneyStep = () => ({
  id: createCustomerJourneyId('step'),
  label: '',
  required: true,
  sortOrder: 0,
  inquiryPriority: 0,
  tasks: [],
  events: [],
  logFields: [],
  fields: [],
});

export const createEmptyCustomerJourneyTask = (index = 0) => ({
  id: createCustomerJourneyId('task'),
  label: index === 0 ? 'Ny oppgave' : '',
  inputType: 'toggle',
  required: true,
  sortOrder: index,
  tasks: [],
  taskStates: [],
});

export const createEmptyCustomerJourneyEvent = (index = 0) => ({
  id: createCustomerJourneyId('event'),
  label: index === 0 ? 'Ny avtale' : '',
  meetingTitle: '',
  required: true,
  sortOrder: index,
  tasks: [],
});

export const createEmptyCustomerJourney = () => ({
  steps: [],
  statuses: [],
});

export const createEmptyNotificationSettings = () => ({
  enabled: false,
  emailDestinations: [],
  emailDestination: '',
});

export const PRODUCT_NOTIFICATION_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

export const normalizeNotificationEmailDestinations = (value, legacyValue = null) => {
  const source = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(/[,\n;]+/u)
      : [];
  if (source.length === 0 && legacyValue) {
    source.push(legacyValue);
  }

  const normalized = [];
  const seen = new Set();
  source.forEach((entry) => {
    const email = String(entry || '').trim().toLowerCase();
    if (!email || seen.has(email)) return;
    seen.add(email);
    normalized.push(email);
  });
  return normalized;
};

export const cloneInboxField = (field = {}) => ({
  ...createEmptyInboxField(),
  key: field.key || '',
  label: field.label || '',
  type: field.type || 'string',
  required: field.required === true,
  collectInChat: field.collectInChat ?? field.collect_in_chat ?? false,
  showInInboxList: field.showInInboxList ?? field.show_in_inbox_list ?? false,
  placeholder: field.placeholder || '',
  helpText: field.helpText || field.help_text || '',
  defaultValue: field.defaultValue ?? field.default_value ?? '',
  options: Array.isArray(field.options)
    ? field.options.map((option) => String(option || '').trim()).filter(Boolean)
    : [],
});

export const cloneCustomerJourneyField = (field = {}, index = 0) => ({
  ...createEmptyCustomerJourneyField(),
  id: normalizeCustomerJourneyId(field.id || field.key) || createCustomerJourneyId('field'),
  label: field.label || '',
  type: field.type || 'string',
  required: field.required !== false,
  sortOrder: Number.isFinite(Number(field.sortOrder ?? field.sort_order)) ? Number(field.sortOrder ?? field.sort_order) : index,
  placeholder: field.placeholder || '',
  helpText: field.helpText || field.help_text || '',
  options: Array.isArray(field.options)
    ? field.options.map((option) => String(option || '').trim()).filter(Boolean)
    : [],
});

export const cloneCustomerJourneyTask = (task = {}, index = 0) => ({
  ...createEmptyCustomerJourneyTask(index),
  id: normalizeCustomerJourneyId(task.id || task.key) || createCustomerJourneyId('task'),
  label: task.label || task.name || task.title || '',
  inputType: normalizeCustomerJourneyTaskInputType(task.inputType ?? task.input_type ?? task.type),
  required: task.required !== false,
  sortOrder: Number.isFinite(Number(task.sortOrder ?? task.sort_order)) ? Number(task.sortOrder ?? task.sort_order) : index,
  tasks: Array.isArray(task.tasks)
    ? task.tasks.map(cloneCustomerJourneyTask)
    : [],
  taskStates: normalizeCustomerJourneyTaskStates(task.taskStates || task.task_states || task.task_state_config),
});

export const cloneCustomerJourneyEvent = (event = {}, index = 0) => ({
  ...createEmptyCustomerJourneyEvent(index),
  id: normalizeCustomerJourneyId(event.id || event.key) || createCustomerJourneyId('event'),
  label: event.label || event.name || event.title || '',
  meetingTitle: event.meetingTitle || event.meeting_title || '',
  required: event.required !== false,
  sortOrder: Number.isFinite(Number(event.sortOrder ?? event.sort_order)) ? Number(event.sortOrder ?? event.sort_order) : index,
  tasks: Array.isArray(event.tasks)
    ? event.tasks.map(cloneCustomerJourneyTask)
    : [],
});

export const cloneCustomerJourneyStep = (step = {}, index = 0) => ({
  ...createEmptyCustomerJourneyStep(),
  id: normalizeCustomerJourneyId(step.id || step.key) || createCustomerJourneyId('step'),
  label: step.label || '',
  required: step.required !== false,
  sortOrder: Number.isFinite(Number(step.sortOrder ?? step.sort_order)) ? Number(step.sortOrder ?? step.sort_order) : index,
  inquiryPriority: normalizeCustomerJourneyPriority(step.inquiryPriority ?? step.inquiry_priority),
  tasks: Array.isArray(step.tasks)
    ? step.tasks.map(cloneCustomerJourneyTask)
    : normalizeCustomerJourneyStepType(step.type) === 'checkbox'
      ? [cloneCustomerJourneyTask(step, 0)]
      : [],
  events: Array.isArray(step.events)
    ? step.events.map(cloneCustomerJourneyEvent)
    : step.type && normalizeCustomerJourneyStepType(step.type) === 'appointment'
      ? [cloneCustomerJourneyEvent(step, 0)]
      : [],
  logFields: Array.isArray(step.logFields || step.log_fields)
    ? (step.logFields || step.log_fields).map(cloneCustomerJourneyField)
    : step.type && normalizeCustomerJourneyStepType(step.type) === 'log' && Array.isArray(step.fields)
      ? step.fields.map(cloneCustomerJourneyField)
      : [],
  fields: Array.isArray(step.logFields || step.log_fields)
    ? (step.logFields || step.log_fields).map(cloneCustomerJourneyField)
    : step.type && normalizeCustomerJourneyStepType(step.type) === 'log' && Array.isArray(step.fields)
      ? step.fields.map(cloneCustomerJourneyField)
      : [],
});

export const mapCustomerJourney = (config = null) => ({
  steps: Array.isArray(config?.steps)
    ? config.steps.map(cloneCustomerJourneyStep)
    : [],
  statuses: normalizeCustomerJourneyStatuses(config?.statuses || config?.terminalStatuses || config?.terminal_statuses),
});

export const mapInboxConfig = (config = null) => {
  const fields = Array.isArray(config?.fields) ? config.fields.map(cloneInboxField) : [];
  const summaryFieldKeys = Array.isArray(config?.summary_field_keys ?? config?.summaryFieldKeys)
    ? (config.summary_field_keys ?? config.summaryFieldKeys)
      .map((key) => String(key || '').trim().toLowerCase())
      .filter(Boolean)
    : fields.filter((field) => field.showInInboxList).map((field) => field.key);
  return { fields, summaryFieldKeys };
};

export const mapNotificationSettings = (settings = null) => {
  const emailDestinations = normalizeNotificationEmailDestinations(
    settings?.email_destinations || settings?.emailDestinations,
    settings?.email_destination || settings?.emailDestination || null,
  );
  return {
    enabled: settings?.enabled === true,
    emailDestinations,
    email_destination: emailDestinations[0] || null,
    emailDestination: emailDestinations[0] || '',
  };
};

export const mapAssignedBot = (bot = {}) => ({
  id: Number(bot.id),
  name: bot.display_title || bot.displayTitle || bot.name || `Bot ${bot.id}`,
  status: bot.status || 'active',
});

export const mapProductItem = (item = {}) => {
  const assignedBotIds = Array.isArray(item.assigned_bot_ids ?? item.assignedBotIds)
    ? (item.assigned_bot_ids ?? item.assignedBotIds)
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id > 0)
    : [];
  const assignedBots = Array.isArray(item.assigned_bots ?? item.assignedBots)
    ? (item.assigned_bots ?? item.assignedBots).map(mapAssignedBot).filter((bot) => bot.id)
    : [];
  return {
    id: Number(item.id),
    name: item.name || '',
    shortDescription: item.short_description || item.shortDescription || '',
    category: item.category || '',
    tags: Array.isArray(item.tags) ? item.tags : [],
    priceAmount: item.price_amount ?? item.priceAmount ?? '',
    priceCurrency: item.price_currency || item.priceCurrency || '',
    priceDisplay: item.price_display || item.priceDisplay || '',
    url: item.url || '',
    imageUrl: item.image_url || item.imageUrl || '',
    sourceKind: item.source_kind || item.sourceKind || 'manual',
    sourceKey: item.source_key || item.sourceKey || '',
    externalProductId: item.external_product_id || item.externalProductId || '',
    chatTitle: item.chat_title || item.chatTitle || '',
    chatDescription: item.chat_description || item.chatDescription || '',
    colorCode: normalizeColor(item.color_code || item.colorCode),
    status: item.status || 'active',
    assignedBotIds,
    assignedBots,
    inboxConfig: mapInboxConfig(item.inbox_config || item.inboxConfig || null),
    customerJourney: mapCustomerJourney(item.customer_journey || item.customerJourney || null),
    notificationSettings: mapNotificationSettings(item.notification_settings || item.notificationSettings || null),
    updatedAt: item.updated_at || item.updatedAt || null,
  };
};

export const mapProposal = (proposal = {}) => ({
  id: Number(proposal.id),
  botId: Number(proposal.bot_id ?? proposal.botId),
  botName: proposal.bot_name || proposal.botName || '',
  status: proposal.status || 'pending',
  proposalType: proposal.proposal_type || proposal.proposalType || 'create',
  proposedName: proposal.proposed_name || proposal.proposedName || '',
  proposedShortDescription: proposal.proposed_short_description || proposal.proposedShortDescription || '',
  proposedCategory: proposal.proposed_category || proposal.proposedCategory || '',
  proposedUrl: proposal.proposed_url || proposal.proposedUrl || '',
  proposedPriceAmount: proposal.proposed_price_amount ?? proposal.proposedPriceAmount ?? null,
  proposedPriceCurrency: proposal.proposed_price_currency || proposal.proposedPriceCurrency || '',
  proposedPriceDisplay: proposal.proposed_price_display || proposal.proposedPriceDisplay || '',
  confidence: proposal.confidence ?? null,
  reason: proposal.reason || '',
  createdAt: proposal.created_at || proposal.createdAt || null,
});

export const createEmptyDraft = () => ({
  id: null,
  name: '',
  shortDescription: '',
  category: '',
  tagsText: '',
  priceAmount: '',
  priceCurrency: '',
  priceDisplay: '',
  url: '',
  imageUrl: '',
  chatTitle: '',
  chatDescription: '',
  colorCode: DEFAULT_PRODUCT_COLOR,
  status: 'active',
  assignedBotIds: [],
  inboxConfig: createEmptyInboxConfig(),
  customerJourney: createEmptyCustomerJourney(),
  notificationSettings: createEmptyNotificationSettings(),
});

export const createDraftFromProduct = (product = null) => {
  if (!product) return createEmptyDraft();
  const mapped = mapProductItem(product);
  return {
    id: mapped.id,
    name: mapped.name,
    shortDescription: mapped.shortDescription,
    category: mapped.category,
    tagsText: mapped.tags.join(', '),
    priceAmount: mapped.priceAmount ?? '',
    priceCurrency: mapped.priceCurrency,
    priceDisplay: mapped.priceDisplay,
    url: mapped.url,
    imageUrl: mapped.imageUrl,
    chatTitle: mapped.chatTitle,
    chatDescription: mapped.chatDescription,
    colorCode: mapped.colorCode,
    status: mapped.status,
    assignedBotIds: mapped.assignedBotIds,
    inboxConfig: mapInboxConfig(mapped.inboxConfig),
    customerJourney: mapCustomerJourney(mapped.customerJourney),
    notificationSettings: mapNotificationSettings(mapped.notificationSettings),
  };
};

export const parseTags = (value) => String(value || '')
  .split(/[,;|]/u)
  .map((tag) => tag.trim())
  .filter(Boolean);

export const buildProductPayload = (draft) => ({
  name: String(draft.name || '').trim(),
  shortDescription: String(draft.shortDescription || '').trim() || null,
  category: String(draft.category || '').trim() || null,
  tags: parseTags(draft.tagsText),
  priceAmount: draft.priceAmount === '' || draft.priceAmount === null ? null : Number(draft.priceAmount),
  priceCurrency: String(draft.priceCurrency || '').trim().toUpperCase() || null,
  priceDisplay: String(draft.priceDisplay || '').trim() || null,
  url: String(draft.url || '').trim() || null,
  imageUrl: String(draft.imageUrl || '').trim() || null,
  chatTitle: String(draft.chatTitle || '').trim() || null,
  chatDescription: String(draft.chatDescription || '').trim() || null,
  colorCode: normalizeColor(draft.colorCode),
  status: draft.status || 'active',
  assignedBotIds: Array.isArray(draft.assignedBotIds) ? draft.assignedBotIds : [],
});

export const buildInboxConfigPayload = (draft) => {
  const emailDestinations = normalizeNotificationEmailDestinations(
    draft.notificationSettings?.emailDestinations,
    draft.notificationSettings?.emailDestination,
  );
  const fields = (draft.inboxConfig?.fields || [])
    .map((field) => ({
      key: String(field.key || '').trim().toLowerCase(),
      label: String(field.label || '').trim(),
      type: field.type || 'string',
      required: field.required === true,
      collectInChat: field.collectInChat === true,
      showInInboxList: field.showInInboxList === true,
      placeholder: String(field.placeholder || '').trim() || null,
      helpText: String(field.helpText || '').trim() || null,
      defaultValue: field.defaultValue === '' ? null : field.defaultValue,
      options: Array.isArray(field.options)
        ? field.options.map((option) => String(option || '').trim()).filter(Boolean)
        : [],
    }))
    .filter((field) => field.key && field.label);
  return {
    fields,
    summaryFieldKeys: fields.filter((field) => field.showInInboxList).map((field) => field.key),
    notificationSettings: {
      enabled: draft.notificationSettings?.enabled === true,
      emailDestinations,
      emailDestination: emailDestinations[0] || null,
    },
  };
};

export const buildCustomerJourneyPayload = (draft) => {
  const getChildSortOrder = (item, fallback) => {
    const raw = Number(item?.sortOrder ?? item?.sort_order);
    return Number.isFinite(raw) ? raw : fallback;
  };
  const steps = (draft.customerJourney?.steps || [])
    .map((step, index) => {
      const tasks = (step.tasks || []).map((task, taskIndex) => ({
        id: normalizeCustomerJourneyId(task.id || task.key) || createCustomerJourneyId('task'),
        label: String(task.label || '').trim(),
        inputType: normalizeCustomerJourneyTaskInputType(task.inputType ?? task.input_type),
        required: task.required !== false,
        sortOrder: getChildSortOrder(task, taskIndex),
        tasks: (task.tasks || []).map((childTask, childTaskIndex) => ({
          id: normalizeCustomerJourneyId(childTask.id || childTask.key) || createCustomerJourneyId('task'),
          label: String(childTask.label || '').trim(),
          inputType: normalizeCustomerJourneyTaskInputType(childTask.inputType ?? childTask.input_type),
          required: childTask.required !== false,
          sortOrder: getChildSortOrder(childTask, childTaskIndex),
          taskStates: normalizeCustomerJourneyTaskStates(childTask.taskStates || childTask.task_states || childTask.task_state_config, { preserveEmptyLabels: true })
            .map((state) => ({ ...state, label: String(state.label || '').trim() }))
            .filter((state) => state.key && state.label),
        })).filter((childTask) => childTask.id && childTask.label),
        taskStates: normalizeCustomerJourneyTaskStates(task.taskStates || task.task_states || task.task_state_config, { preserveEmptyLabels: true })
          .map((state) => ({ ...state, label: String(state.label || '').trim() }))
          .filter((state) => state.key && state.label),
      })).filter((task) => task.id && task.label);
      const events = (step.events || []).map((event, eventIndex) => ({
        id: normalizeCustomerJourneyId(event.id || event.key) || createCustomerJourneyId('event'),
        label: String(event.label || '').trim(),
        meetingTitle: String(event.meetingTitle || '').trim(),
        required: event.required !== false,
        sortOrder: getChildSortOrder(event, tasks.length + eventIndex),
        tasks: (event.tasks || []).map((task, taskIndex) => ({
          id: normalizeCustomerJourneyId(task.id || task.key) || createCustomerJourneyId('task'),
          label: String(task.label || '').trim(),
          inputType: normalizeCustomerJourneyTaskInputType(task.inputType ?? task.input_type),
          required: task.required !== false,
          sortOrder: getChildSortOrder(task, taskIndex),
          tasks: (task.tasks || []).map((field, fieldIndex) => ({
            id: normalizeCustomerJourneyId(field.id || field.key) || createCustomerJourneyId('task'),
            label: String(field.label || '').trim(),
            inputType: normalizeCustomerJourneyTaskInputType(field.inputType ?? field.input_type),
            required: field.required !== false,
            sortOrder: getChildSortOrder(field, fieldIndex),
            taskStates: normalizeCustomerJourneyTaskStates(field.taskStates || field.task_states || field.task_state_config, { preserveEmptyLabels: true })
              .map((state) => ({ ...state, label: String(state.label || '').trim() }))
              .filter((state) => state.key && state.label),
          })).filter((field) => field.id && field.label),
          taskStates: normalizeCustomerJourneyTaskStates(task.taskStates || task.task_states || task.task_state_config, { preserveEmptyLabels: true })
            .map((state) => ({ ...state, label: String(state.label || '').trim() }))
            .filter((state) => state.key && state.label),
        })).filter((task) => task.id && task.label),
      })).filter((event) => event.id && event.label);
      const logFields = (step.logFields || step.fields || []).map((field, fieldIndex) => ({
        id: normalizeCustomerJourneyId(field.id || field.key) || createCustomerJourneyId('field'),
        label: String(field.label || '').trim(),
        type: field.type || 'string',
        required: field.required !== false,
        placeholder: String(field.placeholder || '').trim() || null,
        helpText: String(field.helpText || '').trim() || null,
        options: Array.isArray(field.options)
          ? field.options.map((option) => String(option || '').trim()).filter(Boolean)
          : [],
        sortOrder: getChildSortOrder(field, tasks.length + events.length + fieldIndex),
      })).filter((field) => field.id && field.label);
      return {
        id: normalizeCustomerJourneyId(step.id || step.key) || createCustomerJourneyId('step'),
        label: String(step.label || '').trim(),
        required: step.required !== false,
        sortOrder: index,
        inquiryPriority: normalizeCustomerJourneyPriority(step.inquiryPriority ?? step.inquiry_priority),
        tasks,
        events,
        logFields,
      };
    })
    .filter((step) => step.id && step.label);
  const statuses = normalizeCustomerJourneyStatuses(draft.customerJourney?.statuses || [])
    .map((status) => ({
      ...status,
      label: String(status.label || '').trim(),
      showProgressionStatus: status.showProgressionStatus !== false,
    }))
    .filter((status) => status.key && status.label);
  return { steps, statuses };
};

export const validateDraft = (draft) => {
  if (!String(draft.name || '').trim()) return 'Produktnavn er påkrevd.';
  if (!/^#[0-9a-f]{6}$/i.test(normalizeColor(draft.colorCode))) return 'Fargekode må være gyldig hex.';
  if (draft.priceAmount !== '' && draft.priceAmount !== null && !Number.isFinite(Number(draft.priceAmount))) {
    return 'Pris må være et tall.';
  }
  const emailDestinations = normalizeNotificationEmailDestinations(
    draft.notificationSettings?.emailDestinations,
    draft.notificationSettings?.emailDestination,
  );
  if (draft.notificationSettings?.enabled && emailDestinations.length === 0) {
    return 'Minst én e-postmottaker er påkrevd når varsling er aktivert.';
  }
  if (emailDestinations.some((email) => !PRODUCT_NOTIFICATION_EMAIL_REGEX.test(email))) {
    return 'Alle e-postmottakere må være gyldige e-postadresser.';
  }
  const keys = new Set();
  for (const field of draft.inboxConfig?.fields || []) {
    const key = String(field.key || '').trim().toLowerCase();
    if (!key && !String(field.label || '').trim()) continue;
    if (!key) return 'Alle produktspesifikke felt må ha nøkkel.';
    if (!/^[a-z0-9_]+$/u.test(key)) return 'Feltnøkler kan bare bruke små bokstaver, tall og understrek.';
    if (keys.has(key)) return `Feltnøkkelen "${key}" er brukt flere ganger.`;
    keys.add(key);
    if (!String(field.label || '').trim()) return 'Alle produktspesifikke felt må ha etikett.';
  }
  const journeyStepIds = new Set();
  const journeyStatusKeys = new Set();
  for (const status of draft.customerJourney?.statuses || []) {
    const key = normalizeCustomerJourneyId(status.key);
    if (!key && !String(status.label || '').trim()) continue;
    if (!key) return 'Sluttstatusen mangler intern ID. Oppdater siden og prøv igjen.';
    if (journeyStatusKeys.has(key)) return 'En sluttstatus har duplisert intern ID. Oppdater siden og prøv igjen.';
    journeyStatusKeys.add(key);
    if (!String(status.label || '').trim()) return 'Alle sluttstatuser må ha etikett.';
  }
  for (const step of draft.customerJourney?.steps || []) {
    const id = normalizeCustomerJourneyId(step.id || step.key);
    if (!id && !String(step.label || '').trim()) continue;
    if (!id) return 'Kundeløp-steget mangler intern ID. Oppdater siden og prøv igjen.';
    if (journeyStepIds.has(id)) return 'Et kundeløp-steg har duplisert intern ID. Oppdater siden og prøv igjen.';
    journeyStepIds.add(id);
    if (!String(step.label || '').trim()) return 'Alle kundeløp-steg må ha etikett.';
    const priority = normalizeCustomerJourneyPriority(step.inquiryPriority ?? step.inquiry_priority);
    if (priority < 0 || priority > 100) return 'Prioritet må være mellom 0 og 100.';
    for (const task of step.tasks || []) {
      if (!String(task.label || '').trim()) return 'Alle oppgaver i kundeløp må ha etikett.';
      for (const childTask of task.tasks || []) {
        if (!String(childTask.label || '').trim()) return 'Alle oppgaver i oppgavegrupper må ha etikett.';
      }
    }
    for (const event of step.events || []) {
      if (!String(event.label || '').trim()) return 'Alle avtaler i kundeløp må ha etikett.';
      for (const task of event.tasks || []) {
        if (!String(task.label || '').trim()) return 'Alle oppgaver i avtaler må ha etikett.';
      }
    }
    const logFieldIds = new Set();
    for (const field of (step.logFields || step.fields || [])) {
      const fieldId = normalizeCustomerJourneyId(field.id || field.key);
      if (!fieldId && !String(field.label || '').trim()) continue;
      if (!fieldId) return 'Loggfeltet mangler intern ID. Oppdater siden og prøv igjen.';
      if (logFieldIds.has(fieldId)) return `Et loggfelt har duplisert intern ID i ${step.label || 'kundeløpet'}. Oppdater siden og prøv igjen.`;
      logFieldIds.add(fieldId);
      if (!String(field.label || '').trim()) return 'Alle loggfelt må ha etikett.';
    }
  }
  return null;
};

export const formatPrice = (product) => {
  if (product.priceDisplay) return product.priceDisplay;
  if (product.priceAmount !== '' && product.priceAmount !== null && product.priceCurrency) {
    return `${product.priceAmount} ${product.priceCurrency}`;
  }
  return null;
};

export const ProductSwatch = ({ color, className = '' }) => (
  <span
    className={`product-color-swatch${className ? ` ${className}` : ''}`}
    style={{ backgroundColor: normalizeColor(color) }}
    aria-hidden="true"
  />
);
